import React from "react";
import { Router, Thermometer } from "lucide-react";
import { IQWatchDeviceWithData, RouterStatus } from "../../../types";

interface RouterStatusTabProps {
  deviceWithData: IQWatchDeviceWithData;
  routerStatus: RouterStatus | null;
  loadingRouter: boolean;
}

const RouterStatusTab: React.FC<RouterStatusTabProps> = ({
  deviceWithData,
  routerStatus,
  loadingRouter,
}) => {
  const latestData = deviceWithData.latest_data;

  return (
    <>
      {/* Router Information Section */}
      {routerStatus || latestData?.router_data ? (
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <Router className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-800">
              Router Status
            </h3>
            {loadingRouter && (
              <span className="text-sm text-gray-500">Loading...</span>
            )}
          </div>

          {/* Top Row: Connection and Signal Quality */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Connection Status */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                Connection
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Status:</span>
                  <span
                    className={`text-sm font-medium ${
                      (routerStatus?.connstate ||
                        latestData?.router_data?.input2?.connstate) ===
                      "Connected"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {routerStatus?.connstate ||
                      latestData?.router_data?.input2?.connstate ||
                      "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Network State:</span>
                  <span className="text-sm text-gray-900">
                    {routerStatus?.netstate ||
                      latestData?.router_data?.input2?.netstate ||
                      "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Network:</span>
                  <span className="text-sm text-gray-900">
                    {routerStatus?.network ||
                      latestData?.router_data?.input2?.network ||
                      "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Operator:</span>
                  <span className="text-sm text-gray-900">
                    {routerStatus?.operator ||
                      latestData?.router_data?.input2?.operator ||
                      "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Signal Quality */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                Signal Quality
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">RSSI:</span>
                  <span
                    className={`text-sm font-medium ${(() => {
                      const rssi =
                        routerStatus?.rssi ??
                        latestData?.router_data?.input2?.rssi;
                      if (rssi === undefined) return "text-gray-600";
                      if (rssi >= -50) return "text-green-600";
                      if (rssi >= -60) return "text-green-500";
                      if (rssi >= -70) return "text-yellow-600";
                      return "text-red-600";
                    })()}`}
                  >
                    {routerStatus?.rssi !== undefined
                      ? `${routerStatus.rssi} dBm`
                      : latestData?.router_data?.input2?.rssi !== undefined
                      ? `${latestData.router_data.input2.rssi} dBm`
                      : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">RSRP:</span>
                  <span className="text-sm text-gray-900">
                    {routerStatus?.rsrp !== undefined
                      ? `${routerStatus.rsrp} dBm`
                      : latestData?.router_data?.input2?.rsrp !== undefined
                      ? `${latestData.router_data.input2.rsrp} dBm`
                      : "N/A"}
                  </span>
                </div>
                {latestData?.router_data?.input2?.rsrq !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">RSRQ:</span>
                    <span className="text-sm text-gray-900">
                      {latestData.router_data.input2.rsrq} dB
                    </span>
                  </div>
                )}
                {latestData?.router_data?.input2?.sinr !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">SINR:</span>
                    <span className="text-sm text-gray-900">
                      {latestData.router_data.input2.sinr} dB
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Middle Row: Hardware and Network & Data */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Hardware Information */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                Hardware
              </h4>
              <div className="space-y-3">
                {latestData?.router_data?.input4?.name && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Device Name:</span>
                    <span className="text-sm text-gray-900">
                      {latestData.router_data.input4.name}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">MAC Address:</span>
                  <span className="text-sm text-gray-900 font-mono text-xs">
                    {routerStatus?.mac_address ||
                      latestData?.router_data?.input4?.mac ||
                      "N/A"}
                  </span>
                </div>
                {latestData?.router_data?.input4?.hwver && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Hardware Ver:</span>
                    <span className="text-sm text-gray-900">
                      {latestData.router_data.input4.hwver}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Temperature:</span>
                  <div className="flex items-center space-x-1">
                    <Thermometer className="w-3 h-3 text-orange-500" />
                    <span className="text-sm text-gray-900">
                      {routerStatus?.temp_celsius !== undefined &&
                      routerStatus.temp_celsius !== null
                        ? `${Number(routerStatus.temp_celsius).toFixed(1)}°C`
                        : latestData?.router_data?.input2?.temp !== undefined
                        ? `${(latestData.router_data.input2.temp / 10).toFixed(
                            1
                          )}°C`
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Network & Data Transfer */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                Network & Data
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">IP Address:</span>
                  <span className="text-sm text-gray-900 font-mono text-xs">
                    {routerStatus?.ip_address ||
                      latestData?.router_data?.input2?.ip?.[0] ||
                      "N/A"}
                  </span>
                </div>
                {latestData?.router_data?.input2?.simstate && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">SIM State:</span>
                    <span
                      className={`text-sm font-medium ${
                        latestData.router_data.input2.simstate === "inserted"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {latestData.router_data.input2.simstate}
                    </span>
                  </div>
                )}
                {latestData?.router_data?.input3 && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Data TX:</span>
                      <span className="text-sm text-gray-900">
                        {(() => {
                          const bytes = latestData.router_data.input3.tx;
                          if (bytes === 0) return "0 B";
                          const k = 1024;
                          const sizes = ["B", "KB", "MB", "GB"];
                          const i = Math.floor(Math.log(bytes) / Math.log(k));
                          return (
                            parseFloat((bytes / Math.pow(k, i)).toFixed(1)) +
                            " " +
                            sizes[i]
                          );
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Data RX:</span>
                      <span className="text-sm text-gray-900">
                        {(() => {
                          const bytes = latestData.router_data.input3.rx;
                          if (bytes === 0) return "0 B";
                          const k = 1024;
                          const sizes = ["B", "KB", "MB", "GB"];
                          const i = Math.floor(Math.log(bytes) / Math.log(k));
                          return (
                            parseFloat((bytes / Math.pow(k, i)).toFixed(1)) +
                            " " +
                            sizes[i]
                          );
                        })()}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Row: Device Info and System */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Device Information */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                Device Info
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Model:</span>
                  <span className="text-sm text-gray-900">
                    {routerStatus?.model ||
                      latestData?.router_data?.input2?.model ||
                      "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Serial:</span>
                  <span className="text-sm text-gray-900 font-mono text-xs">
                    {routerStatus?.device_serial ||
                      latestData?.router_data?.input2?.serial ||
                      "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">IMEI:</span>
                  <span className="text-sm text-gray-900 font-mono text-xs">
                    {routerStatus?.imei ||
                      latestData?.router_data?.input2?.imei ||
                      "N/A"}
                  </span>
                </div>
                {routerStatus?.iccid && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">ICCID:</span>
                    <span className="text-sm text-gray-900 font-mono text-xs">
                      {routerStatus.iccid}
                    </span>
                  </div>
                )}
                {latestData?.router_data?.input2?.manuf && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Manufacturer:</span>
                    <span className="text-sm text-gray-900">
                      {latestData.router_data.input2.manuf}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Firmware & Timestamps */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                System
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Firmware:</span>
                  <span className="text-sm text-gray-900 font-mono text-xs">
                    {routerStatus?.firmware ||
                      latestData?.router_data?.input1?.fw ||
                      "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Last Update:</span>
                  <span className="text-sm text-gray-900">
                    {routerStatus?.last_updated_iso
                      ? new Date(routerStatus.last_updated_iso).toLocaleString()
                      : latestData?.router_data?.input1?.local_time || "N/A"}
                  </span>
                </div>
                {latestData?.router_data?.input2?.modem && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Modem:</span>
                    <span className="text-sm text-gray-900">
                      {latestData.router_data.input2.modem}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-center py-12">
            <Router className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Router Information Available
            </h3>
            <p className="text-gray-500">
              This device does not have a router associated with it.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default RouterStatusTab;
